/* v3.18-1293 — Record Offline Payment Dialog
 *
 * Replaces the previous "Mark Entered" one-click action.  Captures:
 *   • Amount
 *   • Processor
 *   • Authorization Code
 *   • Notes (optional)
 *
 * Submits to POST /clients/{id}/credit-card-auth/{rec}/record-offline-payment,
 * which permanently marks the record with an ``offline_payment``
 * sub-document + writes an immutable system_audit row.
 */
import { useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from './ui/dialog';
import { Button }   from './ui/button';
import { Input }    from './ui/input';
import { Label }    from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Landmark, Loader2 } from 'lucide-react';

const PROCESSORS = [
  { key: 'kurv',        label: 'Kurv (Terminal 1)' },
  { key: 'ems',         label: 'EMS Transaction Gateway (Terminal 2)' },
  { key: 'phone_order', label: 'Phone Order (partner processor)' },
  { key: 'other',       label: 'Other / manual' },
];

export default function RecordOfflinePaymentDialog({
  open,
  onOpenChange,
  clientId,
  record,          // { id, amount, ... }
  onRecorded,
}) {
  const [amount, setAmount]         = useState(String(record?.amount || ''));
  const [processor, setProcessor]   = useState('other');
  const [authCode, setAuthCode]     = useState('');
  const [notes, setNotes]           = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) { toast.error('Enter a positive amount'); return; }
    if (!processor) { toast.error('Choose the processor used'); return; }
    if (!authCode.trim()) { toast.error('Authorization code is required'); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(
        `/clients/${clientId}/credit-card-auth/${record.id}/record-offline-payment`,
        {
          amount:              amt,
          processor:           processor,
          authorization_code:  authCode.trim(),
          notes:               notes.trim() || undefined,
        },
      );
      toast.success('Offline payment recorded');
      onRecorded?.(data);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record offline payment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="record-offline-payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Landmark size={16} />
            Record Offline Payment
          </DialogTitle>
          <DialogDescription>
            Permanently marks this authorization as processed outside
            the Hub.  The card data is purged and an audit row is
            written.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label>Amount</Label>
            <Input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              type="number" step="0.01" min="0"
              data-testid="offline-payment-amount"
            />
          </div>
          <div>
            <Label>Processor</Label>
            <Select value={processor} onValueChange={setProcessor}>
              <SelectTrigger data-testid="offline-payment-processor">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PROCESSORS.map((p) => (
                  <SelectItem key={p.key} value={p.key} data-testid={`offline-payment-processor-${p.key}`}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Authorization Code</Label>
            <Input
              value={authCode}
              onChange={(e) => setAuthCode(e.target.value)}
              placeholder="e.g. AUTH123456"
              data-testid="offline-payment-auth-code"
            />
          </div>
          <div>
            <Label>Notes <span className="text-xs text-slate-400">(optional)</span></Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="e.g. Client called in and paid via partner's phone terminal."
              data-testid="offline-payment-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange?.(false)} data-testid="offline-payment-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting} data-testid="offline-payment-submit">
            {submitting ? <Loader2 className="animate-spin h-4 w-4 mr-1" /> : null}
            Record Offline Payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
